import React from 'react';
import styled from 'styled-components';
import { RouterButton } from '../pages/GISOverView';
import { paths } from '../consts';

type GisForManufacturersProps = {
  className?: string;
};

const Col = styled.div`
  width: 100%;
  margin-top: 32px;
`;

const H1 = styled.h1`
  margin: 0 0 14px;
  font-size: 44px;
  line-height: 1.12;
  font-weight: 800;
  letter-spacing: -0.02em;

  @media (max-width: 767px) {
    font-size: 34px;
  }
`;

const H2 = styled.h2`
  margin: 26px 0 10px;
  font-size: 26px;
  line-height: 1.2;
  font-weight: 800;
  letter-spacing: -0.01em;

  @media (max-width: 767px) {
    font-size: 22px;
  }
`;

const P = styled.p`
  margin: 0 0 14px;
  font-size: 16px;
  line-height: 1.75;
  color: rgba(11, 18, 32, 0.86);
`;

const ExternalLink = styled.a`
  color: #b1002a;
  font-weight: 800;
  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;

  &:hover {
    opacity: 0.85;
  }
`;

const LinksList = styled.ul`
  margin: 8px 0 14px 18px;
  padding: 0;

  li {
    margin: 0 0 10px;
    line-height: 1.75;
    color: rgba(11, 18, 32, 0.86);
  }

  li:last-child {
    margin-bottom: 0;
  }
`;

const Underline = styled.span`
  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;
`;

const Buttons = styled.div`
  margin: 12px 0 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const Callout = styled.p`
  margin: 12px 0 0;
  padding: 14px 16px;
  border-radius: 14px;

  background: rgba(177, 0, 42, 0.06);
  border: 1px solid rgba(177, 0, 42, 0.18);

  font-size: 16px;
  line-height: 1.75;
  font-weight: 800;
  color: rgba(11, 18, 32, 0.92);
`;

const EmailLink = styled.a`
  color: #b1002a;
  text-decoration: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;

  &:hover {
    opacity: 0.85;
  }
`;

export const GisForManufacturers: React.FC<GisForManufacturersProps> = ({ className }) => (
        <Col className={className}>
            <H1>GIS For Manufacturers</H1>
            <P>
                For full details on the role of the Manufacturer in the GIS process, view the GIS Policy and
                Procedure Manual, Section 5, Architectural Woodwork Manufacturer.
            </P>
            <H2>Complete the Manufacturer Standards Exam (MSE)</H2>
            <P>
                Before applying for the GIS Certificate, Manufacturer Members must have one owner or manager
                in their company successfully complete the Manufacturer Standards Exam (MSE).
            </P>
            <P>
                <ExternalLink
                    href=""
                    rel="noopener noreferrer"
                >
                    Learn more
                </ExternalLink>
            </P>

            <H2>PROJECT TEMPLATES</H2>

            <LinksList>
                <li>
                    <ExternalLink
                        href=""
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        GIS Form – Inspection Request
                    </ExternalLink>
                </li>
                <li>
                    <ExternalLink
                        href=""
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        GIS Form – Final Inspection Request
                    </ExternalLink>
                </li>
                <li>
                    <ExternalLink
                        href=""
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        GIS Form – Temperature and Humidity
                    </ExternalLink>
                </li>
            </LinksList>

            <P>
                Separate Contracts for Manufacturing and Installation{' '}
                <Underline>
                    <ExternalLink
                        href=""
                        rel="noopener noreferrer"
                    >
                        English
                    </ExternalLink>
                </Underline>{' '}
                <Underline>
                    <ExternalLink
                        href=""
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        French
                    </ExternalLink>
                </Underline>{' '}
                (Letter to Owner)
            </P>

            <H2>GIS Fees</H2>
            <P>
                Your project’s Guarantee and Inspection Service (“GIS”) fee is based on the architectural
                woodwork contract amount, and the GIS cost is part of the tendered lump sum.
            </P>

            <Buttons>
                <RouterButton to={paths.GIS_ESTIMATOR}>
                    GIS Fee Estimator
                </RouterButton>
            </Buttons>

            <P>
                Please note that the GIS Fee Estimator provides an estimate only. Contact your local AWMAC
                Chapter office to confirm if any additional fees apply.
            </P>

            <Callout>
                Need assistance calculating the GIS fee? Please email us at{' '}
                <EmailLink href="mailto:gis@awmacbc.com">gis@awmacbc.com</EmailLink>.
            </Callout>
        </Col>
);
